import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/lib/chunk-load-error';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
      return;
    }
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isStaleChunk = isChunkLoadError(this.state.error);

      if (this.props.fallback && !isStaleChunk) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-100 items-center justify-center p-8">
          <div className="max-w-md text-center">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-warning" />
            <h2 className="mb-2 text-lg font-semibold text-foreground">
              Something went wrong
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              {isStaleChunk
                ? 'A new version of TruSpace was released while this page was open. Reload to get the latest version.'
                : (this.state.error?.message || 'An unexpected error occurred.')}
            </p>
            <Button onClick={isStaleChunk ? this.handleReload : this.handleReset}>
              <RefreshCw />
              {isStaleChunk ? 'Reload page' : 'Try Again'}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
